import captureError from '@disco/utils/captureError'

import React, { useCallback, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'
import { parseServerError } from '@disco/utils/captureError'
import Alert from 'components/ui/alert'
import { transcribeTracks } from '@disco/state-management/actions/ServerActions'
import { Text } from 'components/ds'
import ModalDialog from 'components/ui/modal-dialog'
import Stack from 'components/ui/stack'

const LyricTranscriptionTermsDialog = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ trackId: string }>()
  const trackId = params.trackId!
  const trackIdNum = parseInt(trackId, 10)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const defaultReturnTo = `/explorer/tracks/${trackId}/edit-metadata?tab=1`
  const handleCancel = useCallback(() => {
    navigate(location.state?.returnTo ?? defaultReturnTo)
  }, [navigate])

  const onSubmit = async () => {
    setSubmitting(true)

    try {
      await dispatch(
        updateUserSettings(
          {
            settings: {
              lyric_transcription_terms_accepted_at: new Date().toISOString(),
            },
          },
          false,
        ),
      )
      navigate(location.state?.returnTo ?? defaultReturnTo, { replace: true })
      await dispatch(transcribeTracks([trackIdNum]))
    } catch (error) {
      setError(parseServerError(error?.body?.[0] || error?.body || error))
      dispatch(sendGenericError(error))
      captureError(error)
    }

    setSubmitting(false)
  }

  return (
    <ModalDialog
      title="Lyric Transcription Terms and Conditions"
      width="large"
      primaryAction={{
        caption: 'Accept',
        onClick: onSubmit,
        progressCaption: 'Submitting',
        progressIndicator: submitting,
      }}
      onCancel={handleCancel}
    >
      <Stack space="small">
        <Alert status="warning" icon="warning">
          <strong>Note:</strong> You will not be able to proceed to use this
          Feature without clicking Accept.
        </Alert>
        <Text>
          As a condition for using the Lyric Transcription Feature, you warrant
          and represent that you hold the legal rights to use this Feature, that
          you consent to the reproduction of those rights on the DISCO platform
          and that you consent to the publication of those rights on the DISCO
          platform. Any use of this Feature where you do not possess such rights
          shall be considered a violation of our Terms and Conditions and not
          permitted on our platform. Please review DISCO’s{' '}
          <a
            className="text-link"
            href="https://disco.ac/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms and conditions
          </a>{' '}
          if you have any additional questions on the legal use of this
          platform.
        </Text>
        {error && (
          <div>
            <Alert status="danger" icon="warning">
              {String(error)}
            </Alert>
          </div>
        )}
      </Stack>
    </ModalDialog>
  )
}

export default LyricTranscriptionTermsDialog
